/**
 * "Lög á „{name}“": every song of the open folder / setlist in one place, in the list's shared order
 * (filtered to the songs this user can see, numbered as on the list screen). Opened by tapping the
 * active list tab or the "3 / 12" of "‹ Fyrra · 3 / 12 · Næsta ›".
 *  - At the top, the sheet choice of the open song (when it has more than one sheet).
 *  - The open song is marked (aria-current); entry notes are shown. Tapping a song opens it in this
 *    tab (history replace, page 1).
 *  - Editors and admins (the conductor has role editor) get "Raða lögum" → ReorderSheet; the new
 *    order shows here at once. Not for a practice (its songs are arranged on the event page).
 * Refreshed when opened if the copy is more than LIST_FRESH_MS old (the order is shared).
 */
import { useQueryClient } from '@tanstack/react-query';
import { ArrowUpDown, StickyNote } from 'lucide-react';
import { useEffect, useMemo, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router';
import { roleAtLeast } from '../../shared/audience';
import { practiceEventId } from '../../shared/program';
import type { CollectionEntryDTO, SongListItemDTO } from '../../shared/dto/library';
import { collectionKey, useCollection, useSongs } from '../api/library';
import { useMe } from '../auth/AuthProvider';
import { Button, cx, Sheet } from '../components/ui';
import { useT } from '../i18n';
import { creditsLine } from '../screens/library/format';
import { ReorderSheet } from '../screens/library/ReorderSheet';
import { orderedEntries, setlistSearch } from '../viewer/setlist';
import { SheetChoices, type SheetChoice } from './SheetMenu';

/** A copy older than this is fetched again when the drawer opens. */
export const LIST_FRESH_MS = 5000;

export interface DrawerList {
  collectionId: string;
  /** The open entry (null: none — e.g. the song did not open). */
  entryId: string | null;
  songId: string | null;
}

export function CollectionDrawer({
  open,
  onClose,
  list,
  sheets,
}: {
  open: boolean;
  onClose: () => void;
  list: DrawerList | null;
  /** The open song's sheet choice (shown when it has more than one). */
  sheets?: SheetChoice | null;
}) {
  // Mounted only while open: every opening checks the list's age again.
  return open && list ? <DrawerBody list={list} onClose={onClose} sheets={sheets ?? null} /> : null;
}

interface Row {
  entry: CollectionEntryDTO;
  /** Index among all entries (the ?i= of the link, as CollectionScreen makes it). */
  index: number;
  song: SongListItemDTO;
}

function DrawerBody({ list, onClose, sheets }: { list: DrawerList; onClose: () => void; sheets: SheetChoice | null }) {
  const t = useT();
  const me = useMe();
  const qc = useQueryClient();
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const colQ = useCollection(list.collectionId);
  const songsQ = useSongs();
  const [reordering, setReordering] = useState(false);
  const isEditor = !!me && roleAtLeast(me.role, 'editor') && !practiceEventId(list.collectionId);
  const { refetch } = colQ;

  useEffect(() => {
    const at = qc.getQueryState(collectionKey(list.collectionId))?.dataUpdatedAt ?? 0;
    if (Date.now() - at > LIST_FRESH_MS) void refetch();
  }, [qc, list.collectionId, refetch]);

  const col = colQ.data?.id === list.collectionId ? colQ.data : null;
  const rows = useMemo<Row[]>(() => {
    if (!col) return [];
    const byId = new Map((songsQ.data ?? []).map((s) => [s.id, s]));
    const out: Row[] = [];
    orderedEntries(col.entries).forEach((entry, index) => {
      const song = byId.get(entry.songId);
      if (song) out.push({ entry, index, song });
    });
    return out;
  }, [col, songsQ.data]);

  const open = (r: Row) => {
    onClose();
    if (r.entry.entryId === list.entryId) return;
    navigate(
      { pathname: `/songs/${encodeURIComponent(r.song.id)}`, search: setlistSearch(params, list.collectionId, r.index, r.entry.entryId) },
      { replace: true },
    );
  };

  const multiSheet = !!sheets && sheets.files.length > 1;
  return (
    <>
      <Sheet open onClose={onClose} wide title={t('tabs.list_title', { name: col?.name ?? '' })}>
        <div className="flex flex-col gap-4 pb-2">
          {multiSheet ? (
            <div className="flex flex-col gap-2">
              <h2 className="text-[15px] font-semibold text-[var(--muted)]">{t('tabs.choose_sheet')}</h2>
              <SheetChoices choice={sheets} onChosen={onClose} />
            </div>
          ) : null}
          {isEditor && col && col.entries.length > 1 ? (
            <div className="flex justify-end">
              <Button variant="secondary" icon={ArrowUpDown} onClick={() => setReordering(true)}>
                {t('library.reorder_songs')}
              </Button>
            </div>
          ) : null}
          {rows.length > 0 ? (
            <ol className="divide-y divide-[var(--line)] overflow-hidden rounded-[var(--radius-card)] border border-[var(--line)]">
              {rows.map((r, n) => {
                const current = r.entry.entryId === list.entryId;
                const credits = creditsLine(t, r.song);
                return (
                  <li key={r.entry.entryId}>
                    <button
                      type="button"
                      aria-current={current ? 'true' : undefined}
                      onClick={() => open(r)}
                      className={cx(
                        'flex min-h-14 w-full items-center gap-3 px-4 py-2.5 text-left transition',
                        current ? 'bg-[var(--accent-soft)]' : 'hover:bg-[var(--surface-2)] active:bg-[var(--accent-soft)]',
                      )}
                    >
                      <span
                        className={cx(
                          'flex size-10 shrink-0 items-center justify-center rounded-full text-[17px] font-bold tabular-nums',
                          current ? 'bg-[var(--accent)] text-[var(--accent-ink)]' : 'bg-[var(--accent-soft)] text-[var(--accent)]',
                        )}
                        aria-label={t('library.position', { n: n + 1 })}
                      >
                        {n + 1}
                      </span>
                      <span className="min-w-0 flex-1">
                        <span className={cx('block text-[18px] leading-snug font-semibold break-words', current && 'text-[var(--accent)]')}>{r.song.title}</span>
                        {credits ? <span className="block truncate text-[15px] text-[var(--muted)]">{credits}</span> : null}
                        {r.entry.note ? (
                          <span className="mt-1 flex items-start gap-1.5 text-[15px] text-[var(--ink-2)] italic">
                            <StickyNote className="mt-0.5 size-4 shrink-0 text-[var(--muted)]" aria-hidden />
                            <span className="break-words">{r.entry.note}</span>
                          </span>
                        ) : null}
                      </span>
                    </button>
                  </li>
                );
              })}
            </ol>
          ) : col ? (
            <p className="py-4 text-center text-[var(--muted)]">{t('tabs.no_songs')}</p>
          ) : null}
        </div>
      </Sheet>
      {isEditor ? <ReorderSheet collectionId={list.collectionId} open={reordering} onClose={() => setReordering(false)} /> : null}
    </>
  );
}
